// frontend/src/pages/citizen/CitizenInvestigations.js
// FULL FILE — keeps the existing returned-visit edit flow, centralized read-only handling,
// Save & Continue → Prescriptions, and legacy endpoint tolerance.
// Updated to make investigations page citizen-aware and visit-context-aware without regression.

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Typography, TextField, Grid, Alert, CircularProgress, Divider, IconButton, Box, Chip } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import EncounterHeader from '../../components/EncounterHeader';
// IMPORTANT: matches your actual file path on disk: src/usevisitreadonly.js (lowercase)
import useVisitReadOnly from '../../useVisitReadOnly';
import http from '../../services/http';
import { getSectionModulePlan, moduleLabels } from '../../clinical/visitSectionModules';

function useQuery(){ const { search } = useLocation(); return useMemo(() => new URLSearchParams(search), [search]); }
const jget = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } };

// ---- tenant-safe navigation helpers ----
function getTenantBase() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return m ? m[0] : '';
  } catch {
    return '';
  }
}
function tenantUrl(path) {
  const base = getTenantBase();
  const p = String(path || '');
  const clean = p.startsWith('/') ? p : `/${p}`;
  return `${base}${clean}`.replace(/\/{2,}/g, '/');
}

function getAgeNumber(val) {
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  const s = String(val ?? '').trim();
  if (!s) return null;
  const m = s.match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}
function normalizeGender(val) {
  const s = String(val || '').trim().toLowerCase();
  if (!s) return 'unknown';
  if (['male', 'm', 'man', 'boy'].includes(s)) return 'male';
  if (['female', 'f', 'woman', 'girl'].includes(s)) return 'female';
  if (s.includes('trans')) return 'transgender';
  if (s.includes('non-binary') || s.includes('non binary') || s.includes('nonbinary')) return 'other';
  return s;
}
function getAgeGroup(age) {
  if (age == null) return 'unknown';
  if (age <= 4) return 'under5';
  if (age <= 9) return 'child';
  if (age <= 19) return 'adolescent';
  if (age <= 59) return 'adult';
  return 'geriatric';
}
function ageGroupLabel(group) {
  if (group === 'under5') return 'Under-5';
  if (group === 'child') return '5–9 years';
  if (group === 'adolescent') return '10–19 years';
  if (group === 'adult') return '20–59 years';
  if (group === 'geriatric') return '60+ years';
  return 'Not derived';
}

function formatAgeAtVisit(visit = {}, fallbackYears = null) {
  const days = Number(visit?.ageInDaysAtVisit);
  if (Number.isFinite(days) && days >= 0) {
    if (days < 60) {
      const wholeDays = Math.floor(days);
      return `${wholeDays} day${wholeDays === 1 ? '' : 's'}`;
    }
    if (days < 365.2425) {
      const months = Math.floor(days / 30.4375);
      const remainingDays = Math.max(0, Math.floor(days - (months * 30.4375)));
      return `${months} month${months === 1 ? '' : 's'}${remainingDays ? ` ${remainingDays} day${remainingDays === 1 ? '' : 's'}` : ''}`;
    }
    const years = Math.floor(days / 365.2425);
    const remainingDays = days - Math.floor(years * 365.2425);
    const months = Math.max(0, Math.floor(remainingDays / 30.4375));
    return `${years} year${years === 1 ? '' : 's'}${months ? ` ${months} month${months === 1 ? '' : 's'}` : ''}`;
  }
  if (fallbackYears == null || !Number.isFinite(Number(fallbackYears))) return '';
  return `${Math.floor(Number(fallbackYears))} years`;
}

// Default state for investigations. This now includes a CSV string for ordered
// investigations (listCSV). When submitting, listCSV will be converted to an
// array and stored in the `list` property on the visit. This allows interns
// to specify which tests were ordered (labs, imaging, etc.) and ensures that
// previous visits and approvals can display the ordered list.
const defaults = {
  hemoglobin:'', tlc:'', dc:'',
  bloodSugarFasting:'', bloodSugarPost:'',
  chestXray:'', otherInvestigations:'',
  images:[],
  // Comma-separated list of ordered investigations. This mirrors the
  // `list` array saved in the database (see VisitSummary for usage).
  listCSV:''
};
const ensure = (x = {}) => {
  const source = x && typeof x === 'object' && !Array.isArray(x) ? x : {};
  const legacy =
    source?.contextual?.legacy &&
    typeof source.contextual.legacy === 'object' &&
    !Array.isArray(source.contextual.legacy)
      ? source.contextual.legacy
      : {};

  // Older route versions preserved unknown flat fields under contextual.legacy.
  // Read that bucket first, then let explicit current fields take precedence.
  const merged = { ...legacy, ...source };
  const blood = merged?.blood && typeof merged.blood === 'object' ? merged.blood : {};
  const radiology =
    merged?.radiology && typeof merged.radiology === 'object' ? merged.radiology : {};
  const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);
  const value = (obj, key, fallback = '') => (
    has(obj, key) && obj[key] != null ? obj[key] : fallback
  );

  const ordered = Array.isArray(merged?.list)
    ? merged.list.filter((item) => item != null && String(item).trim())
    : [];

  return {
    ...defaults,
    hemoglobin: value(merged, 'hemoglobin', value(blood, 'hb')),
    tlc: value(merged, 'tlc', value(blood, 'tlc')),
    dc: value(merged, 'dc', value(blood, 'dlc')),
    bloodSugarFasting: value(
      merged,
      'bloodSugarFasting',
      value(blood, 'sugarFasting')
    ),
    bloodSugarPost: value(merged, 'bloodSugarPost', value(blood, 'sugarPP')),
    chestXray: value(merged, 'chestXray', value(radiology, 'xray')),
    otherInvestigations: value(
      merged,
      'otherInvestigations',
      value(merged, 'others')
    ),
    images: Array.isArray(merged?.images) ? merged.images : [],
    listCSV: Array.isArray(merged?.list)
      ? ordered.map((item) => String(item).trim()).join(', ')
      : (typeof merged?.listCSV === 'string' ? merged.listCSV : ''),
  };
};

async function tryGet(urls){
  let lastErr = null;
  for (const url of urls){
    try{
      const r = await http.get(url);
      if (r && r.ok !== false) return r;
      const msg = String(r?.data?.message || '');
      if (r?.status === 404 || /not\s*found|no\s+investigations|empty/i.test(msg)) return { ok:true, data:{} };
      lastErr = new Error(msg || 'Failed to load investigations');
    }catch(e){ lastErr = e; }
  }
  throw lastErr || new Error('Failed to load investigations');
}
async function tryPost(urls, body){
  let lastErr = null;
  for (const url of urls){
    try{
      const r = await http.post(url, body);
      if (r && r.ok !== false && r?.data?.success !== false) return r;
      lastErr = new Error(r?.data?.message || 'Save failed');
    }catch(e){ lastErr = e; }
  }
  throw lastErr || new Error('Save failed');
}

export default function CitizenInvestigations({
  embedded = false,
  visitId: visitIdProp = '',
  citizenId: citizenIdProp = '',
  visit: visitProp = null,
  onSectionDirty,
  onSectionSaved,
  onCanSave,
  onSavingChange,
  registerSave,
} = {}){
  const theme = useTheme();
  const query = useQuery();
  const params = useParams();
  const navigate = useNavigate();

  const user = jget('loggedInUser') || {};
  const role = String(user?.role || user?.userType || '').toLowerCase();
  const isCitizen = role === 'citizen';
  const isIntern = role.includes('intern');

  const [visitId, setVisitId] = useState(() => (
    embedded ? String(visitIdProp || '').trim() : ''
  ));
  const [citizenId, setCitizenId] = useState(() => (
    embedded ? String(citizenIdProp || '').trim() : ''
  ));
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(true);

  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const [openLabs, setOpenLabs] = useState(true);
  const [openSugar, setOpenSugar] = useState(true);
  const [openImaging, setOpenImaging] = useState(true);
  const [openOther, setOpenOther] = useState(true);
  const [openOrders, setOpenOrders] = useState(true);

  const fieldRefs = {
    hemoglobin: useRef(null),
    tlc: useRef(null),
    dc: useRef(null),
    bloodSugarFasting: useRef(null),
    bloodSugarPost: useRef(null),
    chestXray: useRef(null),
    otherInvestigations: useRef(null),
    listCSV: useRef(null),
  };
  const lastFocus = useRef({ name:'', caret:null });

  const fieldProps = theme.palette.mode === 'dark'
    ? { InputLabelProps:{ style:{ color:'#fff' } }, inputProps:{ style:{ color:'#fff' } } }
    : { InputLabelProps:{ style:{ color:'inherit' } }, inputProps:{ style:{ color:'inherit' } } };

  const activeCitizen = useMemo(() => jget('activeCitizen') || {}, []);

  // In Workspace mode the persisted Visit is authoritative. The standalone
  // page keeps its existing browser-context fallbacks for compatibility.
  const resolvedCitizen = useMemo(() => ({
    ...activeCitizen,
    citizenId:
      String(visitProp?.citizenId || citizenIdProp || activeCitizen?.citizenId || '').trim(),
    name: visitProp?.citizenName || activeCitizen?.name || '',
    age:
      visitProp?.ageYearsAtVisit ??
      visitProp?.citizenAge ??
      activeCitizen?.age ??
      '',
    gender: visitProp?.citizenGender || activeCitizen?.gender || '',
  }), [activeCitizen, citizenIdProp, visitProp]);

  const visitContext = useMemo(() => {
    if (embedded && visitProp) {
      return {
        visitId: visitProp.visitId || visitId,
        citizenId: visitProp.citizenId || citizenId,
        visitCategory: visitProp.visitCategory || visitProp.serviceArea || '',
        serviceArea: visitProp.serviceArea || visitProp.visitCategory || '',
        clinicalPathway: visitProp.clinicalPathway || visitProp.clinicalFocus || '',
        clinicalFocus: visitProp.clinicalFocus || visitProp.clinicalPathway || '',
        complaintGroup:
          visitProp.complaintGroup || visitProp.primaryPresentingProblem || '',
        primaryPresentingProblem:
          visitProp.primaryPresentingProblem || visitProp.complaintGroup || '',
        maternalContext: visitProp.maternalContext || '',
        triagePriority: visitProp.triagePriority || '',
        ageGroupAtVisit: visitProp.ageGroupAtVisit || '',
        ageYearsAtVisit: visitProp.ageYearsAtVisit,
        citizenGender: visitProp.citizenGender || '',
        chiefComplaints: Array.isArray(visitProp.chiefComplaints) ? visitProp.chiefComplaints : [],
        activeClinicalModules: Array.isArray(visitProp.activeClinicalModules)
          ? visitProp.activeClinicalModules
          : [],
        additionalPathways: Array.isArray(visitProp.additionalPathways)
          ? visitProp.additionalPathways
          : [],
      };
    }

    const scoped = visitId ? jget(`activeVisitContext:${visitId}`) : null;
    return scoped || jget('activeVisitContext') || {};
  }, [embedded, citizenId, visitId, visitProp]);

  const citizenAge = useMemo(() => getAgeNumber(resolvedCitizen?.age), [resolvedCitizen]);
  const citizenGender = useMemo(
    () => normalizeGender(resolvedCitizen?.gender),
    [resolvedCitizen]
  );
  const citizenAgeGroup = useMemo(() => getAgeGroup(citizenAge), [citizenAge]);

  const visitCategory = useMemo(
    () => String(visitContext?.visitCategory || query.get('visitCategory') || '').trim(),
    [visitContext, query]
  );
  const clinicalPathway = useMemo(
    () => String(visitContext?.clinicalPathway || query.get('clinicalPathway') || '').trim(),
    [visitContext, query]
  );
  const complaintGroup = useMemo(
    () => String(visitContext?.complaintGroup || query.get('complaintGroup') || '').trim(),
    [visitContext, query]
  );

  const activeClinicalModules = useMemo(
    () => Array.isArray(visitContext?.activeClinicalModules) ? visitContext.activeClinicalModules : [],
    [visitContext]
  );
  const investigationPlan = useMemo(
    () => getSectionModulePlan({ modules: activeClinicalModules, section: 'investigations' }),
    [activeClinicalModules]
  );
  const activeModuleLabels = useMemo(
    () => moduleLabels(activeClinicalModules),
    [activeClinicalModules]
  );
  const investigationBlockSet = useMemo(
    () => new Set(investigationPlan.investigationBlocks || []),
    [investigationPlan.investigationBlocks]
  );

  const profileSummary = useMemo(() => {
    const parts = [];
    const ageDisplay = formatAgeAtVisit(visitProp || {}, citizenAge);
    if (ageDisplay) parts.push(`Age ${ageDisplay}`);
    if (citizenAgeGroup !== 'unknown') parts.push(ageGroupLabel(citizenAgeGroup));
    if (resolvedCitizen?.gender) parts.push(String(resolvedCitizen.gender));
    else if (citizenGender !== 'unknown') parts.push(citizenGender);
    return parts.join(' • ');
  }, [citizenAge, citizenAgeGroup, citizenGender, resolvedCitizen, visitProp]);

  const contextChips = useMemo(() => {
    const out = [];
    if (visitCategory) out.push(`Service Area: ${visitCategory}`);
    if (clinicalPathway) out.push(`Clinical Focus: ${clinicalPathway}`);
    if (complaintGroup) out.push(`Primary Presenting Problem: ${complaintGroup}`);
    return out;
  }, [visitCategory, clinicalPathway, complaintGroup]);

  const sugarRecommended = useMemo(
    () => investigationBlockSet.has('sugar') || (
      ['adolescent', 'adult', 'geriatric'].includes(citizenAgeGroup) &&
      /ncd|diabetes|hypertension|cardiometabolic/i.test(`${visitCategory} ${clinicalPathway} ${complaintGroup}`)
    ),
    [investigationBlockSet, citizenAgeGroup, visitCategory, clinicalPathway, complaintGroup]
  );

  const imagingRecommended = useMemo(
    () => investigationBlockSet.has('imaging') || /respiratory|cough|occupational|chest|injury|trauma/i.test(
      `${visitCategory} ${clinicalPathway} ${complaintGroup}`
    ),
    [investigationBlockSet, visitCategory, clinicalPathway, complaintGroup]
  );

  const cbcRecommended = useMemo(
    () => investigationBlockSet.has('cbc') || /fever|infection|child|under-5|anc|pnc|anemia/i.test(
      `${visitCategory} ${clinicalPathway} ${complaintGroup}`
    ) || ['under5', 'child'].includes(citizenAgeGroup),
    [investigationBlockSet, visitCategory, clinicalPathway, complaintGroup, citizenAgeGroup]
  );

  // Resolve visit/citizen and seed activeVisit for this citizen. Workspace
  // props are authoritative when embedded; the standalone page keeps the
  // existing URL/storage resolution path.
  useEffect(() => {
    if (embedded) {
      const cid = String(citizenIdProp || visitProp?.citizenId || '').trim();
      const vid = String(visitIdProp || visitProp?.visitId || '').trim();

      setCitizenId(cid);
      setVisitId(vid);

      if (cid && vid) {
        try {
          localStorage.setItem('visitId', vid);
          localStorage.setItem('activeVisitId', vid);
          localStorage.setItem(`activeVisitId:${cid}`, vid);
          localStorage.setItem('activeCitizenId', cid);
        } catch {}
      }
      return;
    }

    const urlVisitId   = query.get('visitId') || '';
    const urlCitizenId = query.get('citizenId') || params.citizenId || '';
    const activeCitizenLocal = jget('activeCitizen') || {};

    const cid = urlCitizenId || activeCitizenLocal?.citizenId ||
      localStorage.getItem('activeCitizenId') || localStorage.getItem('citizenId') || '';

    const scoped = cid ? localStorage.getItem(`activeVisitId:${cid}`) : '';
    const legacy = localStorage.getItem('activeVisitId') || localStorage.getItem('visitId') || '';
    const vid = urlVisitId || scoped || legacy || '';

    setCitizenId(cid || '');
    setVisitId(vid || '');

    if (cid && vid && scoped === vid && !urlVisitId){
      localStorage.setItem('visitId', vid);
      localStorage.setItem('activeVisitId', vid);
      localStorage.setItem(`activeVisitId:${cid}`, vid);
    }
  }, [
    embedded,
    citizenIdProp,
    visitIdProp,
    visitProp?.citizenId,
    visitProp?.visitId,
    params.citizenId,
    query,
  ]);

  // Read-only logic centralised; OVERRIDE for Interns on returned visits (editReturnedVisit flag)
  const readOnlyByVisit = useVisitReadOnly(visitId, citizenId);
  const editFlag = (typeof window !== 'undefined' && localStorage.getItem('editReturnedVisit')) || '';
  const allowInternEditReturned = isIntern && visitId && editFlag === visitId;
  const readOnly = isCitizen || (readOnlyByVisit && !allowInternEditReturned);

  // Load existing investigations
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!visitId){ setLoading(false); return; }
      setLoading(true); setLoadError('');
      try{
        const r = await tryGet([
          `/api/visits/${encodeURIComponent(visitId)}/investigations`,
          `/api/visit-investigations/${encodeURIComponent(visitId)}`
        ]);
        if (!mounted) return;
        const payload = r?.data?.data || r?.data?.investigations || r?.data || {};
        setForm(ensure(payload));
      }catch(e){ if (mounted) setLoadError(e.message || 'Could not load investigations'); }
      finally{ if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [visitId]);

  // Adjust section emphasis when context changes, without taking away access.
  useEffect(() => {
    setOpenLabs(cbcRecommended || openLabs);
    setOpenSugar(sugarRecommended || openSugar);
    setOpenImaging(imagingRecommended || openImaging);
  }, [cbcRecommended, sugarRecommended, imagingRecommended]); // eslint-disable-line react-hooks/exhaustive-deps

  // Enable floater only when there is some data and editing is allowed
  const hasData = useMemo(() => {
    const shallow = { ...form }; delete shallow.images;
    return Object.values(shallow).some(v => String(v ?? '').trim()) ||
           (Array.isArray(form.images) && form.images.length > 0);
  }, [form]);

  useEffect(() => {
    // Workspace allows an intentionally empty Investigations section to be
    // saved (for example, when no tests are ordered). Standalone Save &
    // Continue keeps its historical "some data entered" requirement.
    const enabled =
      !loading &&
      !readOnly &&
      Boolean(visitId && citizenId) &&
      (embedded || hasData);

    if (typeof onCanSave === 'function') onCanSave(enabled);

    window.dispatchEvent(new CustomEvent('encounter:can-continue', {
      detail: {
        visitId,
        section: 'investigations',
        can: enabled,
        canSave: enabled,
        canContinue: enabled,
        enabled,
      },
    }));
  }, [
    citizenId,
    embedded,
    hasData,
    loading,
    onCanSave,
    readOnly,
    visitId,
  ]);

  const setBusy = useCallback((busy) => {
    if (typeof onSavingChange === 'function') onSavingChange(Boolean(busy));
    window.dispatchEvent(
      new CustomEvent('encounter:busy', {
        detail: { visitId, section: 'investigations', busy: Boolean(busy) },
      })
    );
  }, [onSavingChange, visitId]);

  const save = useCallback(async () => {
    if (readOnly || !visitId || !citizenId) {
      if (!citizenId) setSaveError('Missing citizen');
      return false;
    }

    try{
      setBusy(true);
      setSaveError('');
      setSaveSuccess('');

      // Build payload. Convert listCSV to the ordered-test array while keeping
      // the compatibility CSV value available for older summaries/editors.
      const payload = ensure(form);
      if (typeof payload.listCSV === 'string') {
        payload.list = payload.listCSV
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }

      const response = await tryPost([
        `/api/visits/${encodeURIComponent(visitId)}/investigations`,
        `/api/visit-investigations/${encodeURIComponent(visitId)}`
      ], {
        citizenId,
        ...payload,
      });

      const saved =
        response?.data?.investigations ||
        response?.data?.investigation ||
        response?.data?.data?.investigations ||
        null;

      if (saved && typeof saved === 'object') {
        setForm(ensure(saved));
      }

      setSaveSuccess('Investigations saved');
      setLoadError('');

      if (typeof onSectionSaved === 'function') {
        await onSectionSaved(response?.data || response);
      }
      return true;
    }catch(e){
      setSaveError(e.message || 'Save failed');
      return false;
    }finally{
      setBusy(false);
    }
  }, [
    citizenId,
    form,
    onSectionSaved,
    readOnly,
    setBusy,
    visitId,
  ]);

  // Workspace registers a direct section-save callback. The standalone page
  // still listens to the historical encounter:save event used by the floater.
  useEffect(() => {
    if (typeof registerSave !== 'function') return undefined;
    registerSave(save);
    return () => registerSave(null);
  }, [registerSave, save]);

  // Floater “Save/Continue”
  useEffect(() => {
    const handler = async (e) => {
      const d = e?.detail || {};
      if (d.visitId && d.visitId !== visitId) return;

      if (embedded) {
        // Other mounted Workspace sections also listen for encounter:save.
        // Never let a History/Examination save silently save Investigations.
        if (d.section !== 'investigations') return;
      } else if (d.section && d.section !== 'investigations') {
        return;
      }

      const ok = await save();
      if (!ok) return;

      if (!embedded && d.intent === 'save-continue') {
        // ✅ tenant-safe redirect
        navigate(
          tenantUrl(`/citizen/${encodeURIComponent(citizenId)}/encounter/prescriptions?visitId=${encodeURIComponent(visitId)}`)
        );
      }
    };
    window.addEventListener('encounter:save', handler);
    return () => window.removeEventListener('encounter:save', handler);
  }, [embedded, visitId, citizenId, save, navigate]);

  // Keep focus + caret stable across renders (fixes keystroke/flicker issues)
  const onFieldChange = (name) => (e) => {
    const el = e.target;
    lastFocus.current = { name, caret: { start: el.selectionStart, end: el.selectionEnd } };
    setSaveSuccess('');
    setForm(f => ({ ...f, [name]: el.value }));
    if (typeof onSectionDirty === 'function') onSectionDirty();
  };
  useEffect(() => {
    const { name, caret } = lastFocus.current || {};
    if (!name) return;
    const wrapper = fieldRefs[name]?.current;
    const el = wrapper ? wrapper.querySelector('input,textarea') : null;
    if (el){
      el.focus({ preventScroll:true });
      if (caret && typeof caret.start === 'number' && typeof caret.end === 'number'){
        try { el.setSelectionRange(caret.start, caret.end); } catch {}
      }
    }
  }, [form]);

  const Section = ({ title, open, onToggle, children, helper }) => (
    <Box sx={{ border:'1px solid', borderColor:'divider', borderRadius:1.5, mb:2 }}>
      <Box sx={{ display:'flex', alignItems:'center', px:1.5, py:1, bgcolor:'action.hover', borderTopLeftRadius:6, borderTopRightRadius:6 }}>
        <Typography variant="subtitle1" sx={{ flex:1 }}>{title}</Typography>
        <IconButton size="small" onClick={onToggle} aria-label={open ? 'Collapse' : 'Expand'}>
          {open ? <ExpandLess/> : <ExpandMore/>}
        </IconButton>
      </Box>
      {open && (
        <Box sx={{ p:2 }}>
          {helper ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {helper}
            </Alert>
          ) : null}
          {children}
        </Box>
      )}
    </Box>
  );

  const Input = ({ name, label, type='text', multiline=false, minRows=1 }) => (
    <TextField
      name={name}
      id={name}
      label={label}
      value={form[name]}
      onChange={onFieldChange(name)}
      fullWidth
      multiline={multiline}
      minRows={minRows}
      inputProps={{ inputMode: type==='number' ? 'numeric' : 'text', pattern: type==='number' ? '[0-9]*' : undefined }}
      disabled={readOnly}
      {...fieldProps}
      ref={fieldRefs[name]}
    />
  );

  return (
    <div
      className={
        embedded
          ? ''
          : 'p-6 bg-white dark:bg-[#181a1b] min-h-screen text-black dark:text-white'
      }
    >
      {!embedded && <EncounterHeader visitId={visitId} />}
      {loading ? <CircularProgress/> : (
        <>
          {loadError && <Alert severity="error" sx={{ mb:2 }}>{loadError}</Alert>}
          {saveError && <Alert severity="error" sx={{ mb:2 }}>{saveError}</Alert>}
          {saveSuccess && <Alert severity="success" sx={{ mb:2 }}>{saveSuccess}</Alert>}

          <Typography variant="h5">Investigations {readOnly ? '(Read-only)' : ''}</Typography>
          <Divider sx={{ my:2 }} />

          {!!resolvedCitizen?.citizenId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {resolvedCitizen?.name || 'Citizen selected'}
              {profileSummary ? ` • ${profileSummary}` : ''}
            </Alert>
          )}

          {contextChips.length > 0 && (
            <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mb:2 }}>
              {contextChips.map((item) => (
                <Chip key={item} size="small" color="primary" variant="outlined" label={item} />
              ))}
            </Box>
          )}

          {activeModuleLabels.length > 0 && (
            <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mb:2 }}>
              {activeModuleLabels.map((item) => (
                <Chip key={item.key} size="small" variant="outlined" label={`Focused module: ${item.label}`} />
              ))}
            </Box>
          )}

          {investigationPlan.prompts.length > 0 && (
            <Alert severity="info" sx={{ mb:2 }}>
              <Box component="ul" sx={{ m:0, pl:2.25 }}>
                {investigationPlan.prompts.slice(0, 5).map((prompt) => (
                  <Box component="li" key={prompt} sx={{ mb:0.5 }}>{prompt}</Box>
                ))}
              </Box>
            </Alert>
          )}

          <Alert severity="info" sx={{ mb:2 }}>
            This investigations page is simplified using the selected citizen’s age group and visit context.
            Relevant investigation blocks are highlighted first, while all existing save fields remain available.
          </Alert>

          <Section
            title="Basic Labs (CBC)"
            open={openLabs}
            onToggle={() => setOpenLabs(v => !v)}
            helper={cbcRecommended ? 'This block is prioritised by the active Visit pathway/modules. Enter a result only when the test was clinically indicated or already available.' : ''}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}><Input name="hemoglobin" label="Hemoglobin (g/dL)" type="number" /></Grid>
              <Grid item xs={12} sm={4}><Input name="tlc" label="TLC (×10⁹/L)" type="number" /></Grid>
              <Grid item xs={12} sm={4}><Input name="dc" label="Differential Count (DC)" /></Grid>
            </Grid>
          </Section>

          <Section
            title="Blood Sugar"
            open={openSugar}
            onToggle={() => setOpenSugar(v => !v)}
            helper={sugarRecommended ? 'This block is prioritised by the active Visit pathway/modules. It is not an automatic test order.' : ''}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><Input name="bloodSugarFasting" label="Fasting (mg/dL)" type="number" /></Grid>
              <Grid item xs={12} sm={6}><Input name="bloodSugarPost" label="Post-prandial (mg/dL)" type="number" /></Grid>
            </Grid>
          </Section>

          <Section
            title="Imaging"
            open={openImaging}
            onToggle={() => setOpenImaging(v => !v)}
            helper={imagingRecommended ? 'Imaging is surfaced because of the active pathway/modules; order it only when the clinical findings support it.' : ''}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}><Input name="chestXray" label="Chest X-ray" /></Grid>
            </Grid>
          </Section>

          <Section
            title="Ordered Investigations"
            open={openOrders}
            onToggle={() => setOpenOrders(v => !v)}
            helper="Enter all tests ordered in this visit. These are saved for visit summary, approvals, and previous-visit review."
          >
            <TextField
              name="listCSV"
              id="listCSV"
              label="Enter ordered tests (comma-separated)"
              value={form.listCSV}
              onChange={onFieldChange('listCSV')}
              fullWidth
              multiline
              minRows={2}
              disabled={readOnly}
              {...fieldProps}
              ref={fieldRefs.listCSV}
              placeholder="e.g. CBC, RFT, LFT, X-ray Chest"
            />
            <Box sx={{ mt:1, fontSize:'0.75rem', opacity:0.6 }}>
              Enter test names separated by commas. These will be saved and displayed in visit summaries.
            </Box>
          </Section>

          <Section
            title="Other Investigations / Notes"
            open={openOther}
            onToggle={() => setOpenOther(v => !v)}
          >
            <Input name="otherInvestigations" label="Details" multiline minRows={3} />
          </Section>
        </>
      )}
    </div>
  );
}
